import type { FillBookingDetailsResponse, OrderProductBooking, ProviderOrderProduct } from "@przezmorze/api-types";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useParams } from "react-router-dom";
import type { ProductResponse } from "../admin/products/productsApi";
import { listProducts } from "../admin/products/productsApi";
import { fillBookingDetails, getProviderOrder } from "./orderBookingsApi";

export interface ResolvedOrderProduct {
  readonly productId: string;
  readonly name: string;
  readonly date: string;
  readonly options: { readonly id: string; readonly name: string; readonly quantity: number }[];
  readonly booking?: OrderProductBooking;
}

function resolveProducts(orderProducts: ProviderOrderProduct[], products: ProductResponse[]): ResolvedOrderProduct[] {
  return orderProducts.map((op) => {
    const product = products.find((p) => p.id === op.id);
    return {
      productId: op.id,
      name: product?.name ?? op.id,
      date: op.date,
      options: op.options.map((oo) => {
        const option = product?.options.find((po) => po.id === oo.id);
        return {
          id: oo.id,
          name: option?.name ?? oo.id,
          quantity: oo.quantity,
        };
      }),
      booking: op.booking,
    };
  });
}

interface FillBookingDetailsMutationArgs {
  bookingNumber: string;
  note?: string;
  file?: File;
}

export const useOrderBookings = () => {
  const { enqueueSnackbar } = useSnackbar();
  const { orderId } = useParams<{ orderId: string }>();
  if (!orderId) {
    throw new Error("useOrderBookings requires an orderId route param");
  }

  const {
    data: order,
    isLoading: isOrderLoading,
    error: orderError,
  } = useQuery({
    queryKey: ["orders", orderId, "booking-page"],
    queryFn: async ({ signal }) => getProviderOrder(orderId, signal),
  });

  const { data: products, isLoading: areProductsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: ({ signal }) => listProducts(signal),
  });

  const fillBookingMutation = useMutation<FillBookingDetailsResponse, Error, FillBookingDetailsMutationArgs>({
    mutationFn: ({ bookingNumber, note, file }) => {
      const formData = new FormData();
      formData.append("bookingNumber", bookingNumber);
      if (note) {
        formData.append("note", note);
      }
      if (file) {
        formData.append("file", file);
      }
      return fillBookingDetails(orderId, formData);
    },
    onSuccess: () => {
      enqueueSnackbar("Dane rezerwacji zostały zapisane", { variant: "success" });
    },
    onError: (error) => {
      enqueueSnackbar(`Nie udało się zapisać danych rezerwacji. Powód: ${error.message}`, {
        variant: "error",
      });
    },
  });

  const isLoading = isOrderLoading || areProductsLoading;
  const isNotFound = !isOrderLoading && !!orderError;

  const resolvedProducts = order && products ? resolveProducts(order.products, products) : undefined;
  const hasBookings = fillBookingMutation.isSuccess || (resolvedProducts?.some((p) => !!p.booking) ?? false);

  return {
    isLoading,
    isNotFound,
    order: {
      id: order?.id.userFriendly,
      name: order?.name,
      products: resolvedProducts,
    },
    hasBookings,
    fillBookingDetails: (bookingNumber: string, note?: string, file?: File) =>
      fillBookingMutation.mutateAsync({ bookingNumber, note, file }),
  };
};
